import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  FetchPaymentByIdRequest,
  UpdatePaymentRequest,
  FetchPaymentsRequest,
  FetchPaymentsScopeRequest,
  CreatePaymentRequest,
  DeletePaymentRequest,
} from './types'
import {
  buildPaymentsListUrl,
  fetchAllReceivedPaymentsShared,
  probePaymentsDateRangeSupport as probeDateRangeHttp,
} from '../../../components/billing/paymentsListApi'
import {serializePaymentsScopeKey} from './scopeCache'
import {AxiosError} from 'axios'
import axios from 'axios'
import {
  fetchPaymentByIdUrl,
  createPaymentUrl,
  updatePaymentUrl,
  deletePaymentUrl,
} from '../../api'

export const fetchPayments = createAsyncThunk(
  'payment/fetch',
  async (payload: FetchPaymentsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        buildPaymentsListUrl({
          sortField: payload.sortField,
          sortOrder: payload.sortOrder,
          pageNumber: payload.pageNumber,
          pageCount: payload.pageCount,
          search: payload.search,
          guestPayerName: payload.guestPayerName,
          paymentType: payload.paymentType,
          users: payload.users,
          role: payload.role,
          classroom: payload.classroom,
          paymentMonth: payload.paymentMonth,
          ticketNumberSearch: payload.ticketNumberSearch,
          startDate: payload.startDate,
          endDate: payload.endDate,
        })
      )
      const {payments, totalPages, total} = res.data
      return {
        payments,
        totalPages,
        total,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const probePaymentsDateRangeSupport = createAsyncThunk(
  'payment/probeDateRange',
  async (payload: {role?: string; users?: string[]}) => {
    const supported = await probeDateRangeHttp({
      role: payload.role,
      users: payload.users,
    })
    return {supported}
  }
)

export const fetchPaymentsScope = createAsyncThunk(
  'payment/fetchScope',
  async (payload: FetchPaymentsScopeRequest, {rejectWithValue}) => {
    const key = serializePaymentsScopeKey(payload)
    try {
      const payments = await fetchAllReceivedPaymentsShared({
        sortField: payload.sortField,
        sortOrder: payload.sortOrder,
        search: payload.search,
        guestPayerName: payload.guestPayerName,
        paymentType: 'received',
        users: payload.users,
        role: payload.role || '',
        classroom: payload.classroom,
        paymentMonth: payload.paymentMonth,
        ticketNumberSearch: payload.ticketNumberSearch,
        startDate: payload.startDate,
        endDate: payload.endDate,
      })
      return {
        payments,
        key,
        loadedAt: Date.now(),
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data?.fr
      return rejectWithValue({
        success: false,
        error: message || 'load_failed',
        key,
      })
    }
  }
)

export const fetchPaymentById = createAsyncThunk(
  'payment/fetchById',
  async (payload: FetchPaymentByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(fetchPaymentByIdUrl(payload.id))
      const {payment} = res.data
      return {
        payment,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const createPayment = createAsyncThunk(
  'payment/create',
  async (payload: CreatePaymentRequest, {rejectWithValue, dispatch}) => {
    try {
      const data = new FormData()
      if (payload.paymentTotal) data.append('paymentTotal', payload.paymentTotal.toString())

      if (payload.user) data.append('user', payload.user)
      if (payload.payments) data.append('payments', JSON.stringify(payload.payments))
      if (payload.description) data.append('description', payload.description)
      if (payload.paymentDate) data.append('paymentDate', payload.paymentDate)
      if (payload.classroom) data.append('classroom', payload.classroom)
      if (payload.establishement) data.append('establishement', payload.establishement)
      // User type fields
      if (payload.isRegisteredUser !== undefined) data.append('isRegisteredUser', JSON.stringify(payload.isRegisteredUser))
      if (payload.guestPayerName) data.append('guestPayerName', payload.guestPayerName)
      if (payload.isEntreprise) data.append('isEntreprise', JSON.stringify(payload.isEntreprise))
      if (payload.enterpriseName) data.append('enterpriseName', payload.enterpriseName)
      if (payload.userFirstName) data.append('userFirstName', payload.userFirstName)
      if (payload.userLastName) data.append('userLastName', payload.userLastName)
      if (payload.userCin) data.append('userCin', payload.userCin)
      if (payload.paymentType) data.append('paymentType', payload.paymentType)
      if (payload.nameOfTheBeneficiary)
        data.append('nameOfTheBeneficiary', payload.nameOfTheBeneficiary)
      if (payload.responsilbe) data.append('responsilbe', payload.responsilbe)
      // Expense-specific fields
      if (payload.expenseId) data.append('expenseId', payload.expenseId)
      if (payload.referenceNumber) data.append('referenceNumber', payload.referenceNumber)
      if (payload.paymentMode) data.append('paymentMode', payload.paymentMode)
      if (payload.status) data.append('status', payload.status)
      if (payload.attachements) {
        payload.attachements.forEach((file: any) => {
          if (file instanceof File) data.append('files', file)
        })
      }
      
      const res = await axios.post(createPaymentUrl, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        /*onUploadProgress: (progressEvent) => {
          const {loaded, total} = progressEvent
          if (total) {
            const percentCompleted = Math.round((loaded * 100) / total)
            dispatch(actions.handleRequestProgress({progress: percentCompleted}))
          }

          //dispatch({ type: 'UPLOAD_PROGRESS', payload: percentCompleted });
        },*/
      })

      const {payment} = res.data
      return {
        payment,
        success: true,
      }
    } catch (error: any) {
      console.error('❌ Create Payment Error:', error)
      console.error('❌ Error Response:', error.response?.data)
      console.error('❌ Error Status:', error.response?.status)
      return rejectWithValue({
        success: false,
        error: error.response?.data || JSON.stringify(error),
      })
    }
  }
)

export const updatePayment = createAsyncThunk(
  'payment/update',
  async (payload: UpdatePaymentRequest, {rejectWithValue}) => {
    try {
      const data = new FormData()
      if (payload.id) data.append('id', payload.id)
      if (payload.user) data.append('user', payload.user)
      if (payload.paymentTotal) data.append('paymentTotal', payload.paymentTotal.toString())
      if (payload.payments) data.append('payments', JSON.stringify(payload.payments))
      if (payload.paymentDate) data.append('paymentDate', payload.paymentDate)
      if (payload.classroom) data.append('classroom', payload.classroom)
      if (payload.establishement) data.append('establishement', payload.establishement)
      // User type fields
      if (payload.isRegisteredUser !== undefined) data.append('isRegisteredUser', JSON.stringify(payload.isRegisteredUser))
      if (payload.guestPayerName) data.append('guestPayerName', payload.guestPayerName)
      if (payload.isEntreprise) data.append('isEntreprise', JSON.stringify(payload.isEntreprise))
      if (payload.enterpriseName) data.append('enterpriseName', payload.enterpriseName)
      if (payload.enterpriseFiscalNumber)
        data.append('enterpriseFiscalNumber', payload.enterpriseFiscalNumber)
      if (payload.enterpriseInfo) data.append('enterpriseInfo', payload.enterpriseInfo)
      if (payload.userFirstName) data.append('userFirstName', payload.userFirstName)
      if (payload.userLastName) data.append('userLastName', payload.userLastName)
      if (payload.userCin) data.append('userCin', payload.userCin)
      if (payload.nameOfTheBeneficiary)
        data.append('nameOfTheBeneficiary', payload.nameOfTheBeneficiary)
      if (payload.responsilbe) data.append('responsilbe', payload.responsilbe)
      if (payload.description) data.append('description', payload.description)
      if (payload.paymentType) data.append('paymentType', payload.paymentType)
      // Expense-specific fields
      if (payload.expenseId) data.append('expenseId', payload.expenseId)
      if (payload.referenceNumber) data.append('referenceNumber', payload.referenceNumber)
      if (payload.paymentMode) data.append('paymentMode', payload.paymentMode)
      if (payload.status) data.append('status', payload.status)
      if (payload.attachements) {
        payload.attachements.forEach((file: any) => {
          if (file instanceof File) data.append('files', file)
        })
      }
      if (payload.deletedAttachements) {
        data.append('deletedAttachements', JSON.stringify(payload.deletedAttachements))
      }
      const res = await axios.post(updatePaymentUrl, data)
      const {payment} = res.data
      return {
        payment,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const deletePayment = createAsyncThunk(
  'payment/delete',
  async (payload: DeletePaymentRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(deletePaymentUrl, payload)
      const {payment} = res.data
      return {
        payment,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)
